import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
	fetchInterventionItems,
	fetchConsultationNotes,
	type InterventionItem,
} from '@/api/adapters/patients';
import type { PatientListItem } from '@/api/adapters/patients';
import { useRuntimeNotesStore } from '@/app/store/runtime-notes.store';

/**
 * Hook: fetch intervention items for a patient, merged with runtime notes.
 *
 * @param patient  The patient whose timeline to fetch.
 * @param filters  Optional BE-side filters (status + period).  Omit or pass
 *                 `{ status: 'ALL', period: 'ALL' }` for the full timeline.
 */
export function useIntervention(
	patient: PatientListItem,
	filters?: { status?: string; period?: string },
) {
	const baseItems = useQuery<InterventionItem[]>({
		queryKey: ['intervention', 'items', patient.uid, filters],
		queryFn: () => fetchInterventionItems(patient, filters),
		staleTime: 60_000,
	});

	const runtimeNotes = useRuntimeNotesStore((s) => s.notes[patient.uid]);

	// Merge base + runtime, runtime notes appear first
	const allItems = useMemo(() => {
		const base = baseItems.data ?? [];
		if (!runtimeNotes) return base;
		const runtime = runtimeNotes.map<InterventionItem>((n) => ({
			kind: 'note' as const,
			status: 'completed' as const,
			dt: `${n.date}T${n.time}:00`,
			typeLabel: n.type === 'call' ? '전화' : '메시지',
			title: n.title,
			text: n.text,
			staff: n.staff,
			staffRole: n.staffRole,
			id: n.id,
			// SHCT-809 — same shape the server uses, so a runtime row can never collide with a fetched one.
			rowKey: `note-${n.id}`,
		}));
		return [...runtime, ...base];
	}, [baseItems.data, runtimeNotes]);

	return { ...baseItems, data: allItems };
}

/**
 * Hook: note lookup map (base + runtime) for panel open.
 */
export function useNoteMap(patientUid: string) {
	const notesQuery = useQuery({
		queryKey: ['intervention', 'notes', patientUid],
		queryFn: () => fetchConsultationNotes(patientUid),
		staleTime: 60_000,
	});

	const runtimeNotes = useRuntimeNotesStore((s) => s.notes[patientUid]);

	return useMemo(() => {
		const m = new Map<
			string,
			Awaited<ReturnType<typeof fetchConsultationNotes>>[number]
		>();
		if (notesQuery.data) {
			for (const n of notesQuery.data) m.set(n.id, n);
		}
		if (runtimeNotes) {
			for (const n of runtimeNotes) m.set(n.id, n);
		}
		return m;
	}, [notesQuery.data, runtimeNotes]);
}
